/**
 * RateCardPanel — admin UI to manage day-rate cards used for
 * deterministic plan pricing.
 *
 * Lets the consultant:
 *   - List all rate cards (with default badge)
 *   - Pick one to edit (or clone it first to preserve the seed)
 *   - Edit every role, day-rate, utilisation %, tier (CardEditor)
 *   - Edit region / sector / engagement-size modifiers (ModifierGroup)
 *   - Make a card the default
 *   - Delete (blocks delete on default)
 *   - Live-preview the price for a sample resource model (PricingPreview)
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Copy, Trash2, Star, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import CardEditor from "./rateCard/CardEditor";
import PricingPreview from "./rateCard/PricingPreview";

export default function RateCardPanel() {
  const [cards, setCards] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/rate-cards");
      setCards(data.items || []);
      if (!selectedId && data.items?.length) {
        const def = data.items.find((c) => c.is_default) || data.items[0];
        setSelectedId(def.id);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rate card list failed");
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => cards.find((c) => c.id === selectedId), [cards, selectedId]);

  const clone = async (id) => {
    try {
      const { data } = await api.post(`/admin/rate-cards/${id}/clone`);
      toast.success("Card cloned");
      setSelectedId(data.id);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };
  const makeDefault = async (id) => {
    try {
      await api.post(`/admin/rate-cards/${id}/make-default`);
      toast.success("Default updated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this rate card? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/rate-cards/${id}`);
      toast.success("Card deleted");
      setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) return <div className="text-zinc-500 text-[13px]">Loading rate cards…</div>;

  return (
    <div className="space-y-5" data-testid="rate-cards-panel">
      <div className="bg-white border border-zinc-200">
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-700">Rate cards</div>
          <p className="text-[11.5px] text-zinc-500 mt-1">
            Day-rates and modifiers used to <strong>deterministically</strong> price the Opus 4.8 project plans. The default card is applied unless a run has an explicit override. Edits to the default card affect plans generated after the change — older plans keep their original audit.
          </p>
        </div>
        <ul>
          {cards.map((c) => (
            <li
              key={c.id}
              className={`flex items-center justify-between border-b border-zinc-100 px-4 py-2 cursor-pointer ${
                c.id === selectedId ? "bg-[#FAF7FF]" : "hover:bg-zinc-50"
              }`}
              onClick={() => setSelectedId(c.id)}
              data-testid={`card-row-${c.id}`}
            >
              <div>
                <div className="text-[13px] font-medium text-zinc-900 flex items-center gap-2">
                  {c.name}
                  {c.is_default && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold bg-[#7737F6] text-white px-1.5 py-0.5">
                      <Star size={9}/> DEFAULT
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {c.currency} · {c.roles?.length || 0} roles ·
                  {c.region_modifiers?.length || 0} region · {c.sector_modifiers?.length || 0} sector ·
                  {c.size_modifiers?.length || 0} size modifiers
                </div>
              </div>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {!c.is_default && (
                  <button
                    type="button"
                    onClick={() => makeDefault(c.id)}
                    className="text-[11px] text-zinc-600 hover:text-[#7737F6] inline-flex items-center gap-1 px-2 py-1"
                    data-testid={`card-make-default-${c.id}`}
                  >
                    <Star size={11}/> default
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => clone(c.id)}
                  className="text-[11px] text-zinc-600 hover:text-[#7737F6] inline-flex items-center gap-1 px-2 py-1"
                  data-testid={`card-clone-${c.id}`}
                >
                  <Copy size={11}/> clone
                </button>
                {!c.is_default && (
                  <button
                    type="button"
                    onClick={() => remove(c.id)}
                    className="text-[11px] text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 px-2 py-1"
                    data-testid={`card-delete-${c.id}`}
                  >
                    <Trash2 size={11}/>
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {selected && (
        <div className="grid grid-cols-[2fr_1fr] gap-4 items-start">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-zinc-700 mb-2 flex items-center gap-2">
              <Edit3 size={11}/> Editing: {selected.name}
            </div>
            <CardEditor card={selected} onSaved={load} />
          </div>
          <div className="sticky top-0">
            <PricingPreview cardId={selected.id} currency={selected.currency || "GBP"} />
          </div>
        </div>
      )}
    </div>
  );
}
